import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
    ArrowLeft, ArrowRight, Check, X, Loader2, Save, Copy, Mail,
    Calendar as CalendarIcon, Timer, Lock,
} from "lucide-react";
import { format } from "date-fns";
import { NoxnLogo } from "@/components/noxn/NoxnLogo";

import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Calendar } from "@/components/ui/calendar";
import { NOXN } from "@/constants/testIds";
import { AU_CITIES, SCREEN_TYPES } from "@/data/locations";
import { useAuth, api } from "@/lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_NOTES = 1200;

const startOfToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

const goals = [
    { value: "product_launch", label: "Launch a new product" },
    { value: "brand_awareness", label: "Build brand awareness" },
    { value: "event_promo", label: "Promote an event" },
    { value: "local_traffic", label: "Drive local foot traffic" },
    { value: "other", label: "Something else" },
];

const formatAud = (n) =>
    new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n);

const HOLD_TIMER_KEY = "noxn.holdTimer";
export const readHoldTimer = () => {
    try {
        const raw = localStorage.getItem(HOLD_TIMER_KEY);
        if (!raw) return null;
        const t = JSON.parse(raw);
        if (!t?.expires_at || new Date(t.expires_at).getTime() <= Date.now()) {
            localStorage.removeItem(HOLD_TIMER_KEY);
            return null;
        }
        return t;
    } catch (e) { return null; }
};
export const clearHoldTimer = () => { try { localStorage.removeItem(HOLD_TIMER_KEY); } catch (e) { /* ignore */ } };

// Live mm:ss ticker for the 20-minute checkout hold
const HoldTicker = ({ iso, onExpire, className = "", testid = "hold-ticker" }) => {
    const [tick, setTick] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setTick(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    const ms = new Date(iso).getTime() - tick;
    useEffect(() => { if (ms <= 0 && onExpire) onExpire(); }, [ms <= 0]);
    if (ms <= 0) return <span data-testid={testid} className={className}>00:00</span>;
    const s = Math.floor(ms / 1000);
    const mm = Math.floor(s / 60).toString().padStart(2, "0");
    const ss = (s % 60).toString().padStart(2, "0");
    return <span data-testid={testid} className={`tabular-nums ${className}`}>{mm}:{ss}</span>;
};

// ==================================================
// Zod schema (superRefine covers conditional rules)
// ==================================================
const fullSchema = z.object({
    campaign_goal: z.string().min(1, "Pick a campaign goal"),
    goal_other_text: z.string().optional(),
    ad_space: z.object({
        cities: z.array(z.string()).min(1, "Pick at least one city"),
        preferred_suburbs: z.string().optional(),
        screen_types: z.array(z.string()).min(1, "Pick at least one screen type"),
    }),
    budget_amount_aud: z.number({ required_error: "Pick a budget" }).int()
        .min(250, "Pick a budget between $250 and $20,000")
        .max(20000, "Pick a budget between $250 and $20,000"),
    launch_date: z.date({ required_error: "Pick a launch date" }),
    business_name: z.string().min(1, "Required"),
    contact_name: z.string().min(1, "Required"),
    email: z.string().email("Enter a valid email"),
    phone: z.string().optional(),
    notes: z.string().max(MAX_NOTES, `Keep it under ${MAX_NOTES} characters`).optional(),
}).superRefine((data, ctx) => {
    if (data.campaign_goal === "other" && !data.goal_other_text?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["goal_other_text"], message: "Tell us your campaign goal" });
    }
    if (data.launch_date && data.launch_date <= startOfToday()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["launch_date"], message: "Pick a future date" });
    }
});

// Fields validated on each step (indexed by step id)
const STEP_FIELDS = {
    goal: ["campaign_goal", "goal_other_text"],
    placement: ["ad_space"],
    date_budget: ["budget_amount_aud", "launch_date"],
    contact: ["business_name", "contact_name", "email", "phone", "notes"],
    summary: [],
};

// ============================================================
// Main component
// ============================================================
export const AssessmentForm = ({ open, onOpenChange, resumeSnapshot = null, initialScreen = null }) => {
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [selectedScreenIds, setSelectedScreenIds] = useState([]);
    const [redirecting, setRedirecting] = useState(null); // {url, expiresAt, heldCount}
    const [activeHold, setActiveHold] = useState(null);   // resumed hold from a prior checkout
    const [savingDraft, setSavingDraft] = useState(false);
    const [savedDraft, setSavedDraft] = useState(null); // { resume_path, token, emailed }
    const [referral, setReferral] = useState(null); // { code, discount_cents }
    const [accountCredit, setAccountCredit] = useState(0); // spendable balance for the signed-in user
    const { user } = useAuth();

    // Fetch spendable account credit + personalise contact details for signed-in users.
    useEffect(() => {
        if (!open || !user) return;
        api.get("/account/loyalty")
            .then(({ data }) => setAccountCredit(data.available_credit_cents || 0))
            .catch(() => setAccountCredit(0));
        if (!getValues("email")) setValue("email", user.email || "");
        if (!getValues("contact_name") && user.name) setValue("contact_name", user.name);
        if (!getValues("business_name") && user.business_name) setValue("business_name", user.business_name);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, user]);

    const stepIds = ["goal", "placement", "date_budget", "contact", "summary"];

    const currentStepId = stepIds[step];
    const totalSteps = stepIds.length;
    const progress = ((step + 1) / totalSteps) * 100;

    const {
        register, handleSubmit, trigger, watch, setValue, reset, control, getValues,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(fullSchema),
        mode: "onTouched",
        defaultValues: {
            campaign_goal: "",
            goal_other_text: "",
            ad_space: { cities: [], preferred_suburbs: "", screen_types: [] },
            budget_amount_aud: 2500,
            launch_date: undefined,
            business_name: "",
            contact_name: "",
            email: "",
            phone: "",
            notes: "",
        },
    });

    const values = watch();

    // Seed a map/rate-card preselected screen: lock in its city + screen id
    useEffect(() => {
        if (!open || !initialScreen) return;
        if (initialScreen.city) {
            setValue("ad_space.cities", [initialScreen.city], { shouldValidate: false });
        }
        if (initialScreen.id) {
            setSelectedScreenIds([initialScreen.id]);
        }
    }, [open, initialScreen, setValue]);

    // Resume a still-live hold from a previous checkout attempt
    useEffect(() => {
        if (open) setActiveHold(readHoldTimer());
    }, [open]);

    // Validate a stored referral code (from a /?ref=CODE share link)
    useEffect(() => {
        if (!open) return;
        try {
            const raw = localStorage.getItem("noxn.referralCode");
            if (!raw) return;
            const { code } = JSON.parse(raw);
            if (!code) return;
            axios.get(`${API}/referral/validate/${encodeURIComponent(code)}`)
                .then(({ data }) => {
                    if (data?.valid) setReferral({ code, discount_cents: data.discount_cents });
                    else localStorage.removeItem("noxn.referralCode");
                })
                .catch(() => {});
        } catch (e) { /* ignore */ }
    }, [open]);

    // Restore a saved snapshot after a cancelled payment
    useEffect(() => {
        if (!open || !resumeSnapshot?.values) return;
        const v = resumeSnapshot.values;
        Object.entries(v).forEach(([k, val]) => {
            if (k === "launch_date" && typeof val === "string") {
                try {
                    const d = new Date(val);
                    if (!Number.isNaN(d.getTime())) setValue(k, d);
                } catch (e) { /* ignore */ }
            } else {
                setValue(k, val);
            }
        });
        // Jump to the saved step (server drafts) or straight to summary (post-cancel retry)
        const summaryIdx = stepIds.length - 1;
        const target = Number.isInteger(resumeSnapshot.step)
            ? Math.min(Math.max(resumeSnapshot.step, 0), summaryIdx)
            : summaryIdx;
        setStep(target);
        try { sessionStorage.removeItem("noxn.pendingForm"); } catch (e) { /* ignore */ }
    }, [open, resumeSnapshot]);

    const isOther = values.campaign_goal === "other";

    const handleClose = () => {
        onOpenChange(false);
        setTimeout(() => { setStep(0); setDone(false); reset(); setSelectedScreenIds([]); }, 300);
    };

    const next = async () => {
        const fields = STEP_FIELDS[currentStepId] || [];
        const valid = await trigger(fields);
        if (!valid) return;
        setStep((s) => Math.min(s + 1, totalSteps - 1));
    };

    const prev = () => setStep((s) => Math.max(s - 1, 0));

    // Compute pricing breakdown from current form values
    const buildBreakdown = () => {
        const items = [];
        if (values.budget_amount_aud) {
            items.push({
                label: "Ad space media budget",
                amount_cents: Math.round(values.budget_amount_aud * 100),
            });
        }
        const subtotal = items.reduce((acc, i) => acc + i.amount_cents, 0);
        const gst = Math.round(subtotal * 0.10);
        let discount = 0;
        if (referral?.discount_cents && subtotal + gst >= referral.discount_cents + 100) {
            discount = referral.discount_cents;
        }
        // Account credit auto-applies when checking out with the account email.
        let credit = 0;
        const formEmail = (watch("email") || "").trim().toLowerCase();
        if (accountCredit > 0 && user?.email && formEmail === user.email.toLowerCase()) {
            credit = Math.min(accountCredit, Math.max(subtotal + gst - discount - 100, 0));
        }
        const total = subtotal + gst - discount - credit;
        return { items, subtotal, gst, discount, credit, total, referral_code: discount > 0 ? referral.code : null };
    };

    const saveDraft = async () => {
        setSavingDraft(true);
        try {
            const v = watch();
            const { data } = await axios.post(`${API}/wizard-drafts`, {
                values: {
                    ...v,
                    launch_date: v.launch_date instanceof Date ? v.launch_date.toISOString() : v.launch_date,
                },
                category: "cat2",
                initial_package: null,
                step,
                email: v.email || null,
            });
            setSavedDraft(data);
        } catch (e) {
            toast.error("Could not save your draft. Try again.");
        } finally {
            setSavingDraft(false);
        }
    };

    const onSubmit = async (data) => {
        // Guard: never submit unless the user is on the summary step and pressed the Submit button.
        if (currentStepId !== "summary") return;
        setSubmitting(true);
        try {
            const payload = {
                form_category: "cat2",
                campaign_goal: data.campaign_goal,
                goal_other_text: data.campaign_goal === "other" ? data.goal_other_text : null,
                services: ["ad_space"],
                creative_files: [],
                ad_space: {
                    cities: data.ad_space?.cities || [],
                    preferred_suburbs: data.ad_space?.preferred_suburbs || null,
                    screen_types: data.ad_space?.screen_types || [],
                },
                selected_package: null,
                budget_amount_aud: data.budget_amount_aud,
                launch_date: data.launch_date instanceof Date ? format(data.launch_date, "yyyy-MM-dd") : data.launch_date,
                business_name: data.business_name,
                contact_name: data.contact_name,
                email: data.email,
                phone: data.phone,
                notes: data.notes,
            };

            const breakdown = buildBreakdown();
            if (breakdown.subtotal < 100) {
                toast.error("Set a media budget to proceed to payment.");
                setSubmitting(false);
                return;
            }

            // Persist form snapshot so the user can retry after cancel without losing data
            try {
                sessionStorage.setItem(
                    "noxn.pendingForm",
                    JSON.stringify({
                        savedAt: new Date().toISOString(),
                        values: {
                            ...data,
                            launch_date: data.launch_date instanceof Date
                                ? data.launch_date.toISOString()
                                : data.launch_date,
                        },
                    })
                );
            } catch (e) { /* ignore quota errors */ }

            const { data: checkout } = await axios.post(`${API}/payments/checkout`, {
                origin_url: window.location.origin,
                campaign: payload,
                line_items: breakdown.items,
                screen_ids: selectedScreenIds,
                referral_code: breakdown.referral_code,
            });

            if (!checkout?.checkout_url) {
                throw new Error("No checkout URL returned.");
            }
            const heldCount = (checkout.held_screen_ids || []).length;
            if (heldCount > 0 && checkout.hold_expires_at) {
                try {
                    localStorage.setItem(HOLD_TIMER_KEY, JSON.stringify({
                        session_id: checkout.session_id,
                        expires_at: checkout.hold_expires_at,
                        held_count: heldCount,
                        saved_at: new Date().toISOString(),
                    }));
                } catch (e) { /* ignore quota errors */ }
                // Show the hold countdown interstitial, then hand off to Stripe.
                setRedirecting({ url: checkout.checkout_url, expiresAt: checkout.hold_expires_at, heldCount });
                setTimeout(() => { window.location.href = checkout.checkout_url; }, 2600);
                return;
            }
            // No screens held — redirect straight to Stripe hosted checkout
            window.location.href = checkout.checkout_url;
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Could not start payment. Try again.";
            toast.error(typeof msg === "string" ? msg : "Could not start payment. Try again.");
            setSubmitting(false);
        }
    };

    const toggleCity = (city) => {
        const cur = values.ad_space?.cities || [];
        setValue("ad_space.cities",
            cur.includes(city) ? cur.filter((v) => v !== city) : [...cur, city],
            { shouldValidate: true });
    };

    const toggleScreenType = (s) => {
        const cur = values.ad_space?.screen_types || [];
        setValue("ad_space.screen_types",
            cur.includes(s) ? cur.filter((v) => v !== s) : [...cur, s],
            { shouldValidate: true });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={NOXN.formModal}
                className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto bg-[#0F1E3B] border border-white/10 p-0 gap-0 rounded-none [&>button]:hidden"
            >
                <DialogTitle className="sr-only">NOXN Campaign Request</DialogTitle>
                <DialogDescription className="sr-only">Let&apos;s start prepping your campaign</DialogDescription>

                {savedDraft && (
                    <div
                        data-testid="draft-saved-overlay"
                        className="fixed inset-0 z-[60] bg-[#0F1E3B]/97 backdrop-blur-sm flex flex-col items-center justify-center text-center px-8"
                    >
                        <div className="w-12 h-12 border border-[#FF5A00]/50 flex items-center justify-center mb-5">
                            <Save size={20} className="text-[#FF5A00]" />
                        </div>
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#FF5A00] mb-3">
                            / Draft saved
                        </div>
                        <div className="font-display font-black text-3xl text-white tracking-tight">Pick it up anywhere.</div>
                        <p className="mt-3 text-sm text-slate-400 max-w-sm">
                            Open this private link on any device within 30 days to continue exactly where you left off.
                        </p>
                        <div className="mt-6 flex items-stretch w-full max-w-md">
                            <input
                                readOnly
                                value={`${window.location.origin}${savedDraft.resume_path}`}
                                data-testid="draft-resume-link"
                                onFocus={(e) => e.target.select()}
                                className="flex-1 bg-[#1B2E55] border border-white/10 text-white px-3 py-2.5 text-xs font-mono-tech min-w-0"
                            />
                            <button
                                type="button"
                                data-testid="draft-copy-btn"
                                onClick={() => {
                                    navigator.clipboard?.writeText(`${window.location.origin}${savedDraft.resume_path}`)
                                        .then(() => toast.success("Link copied to clipboard."))
                                        .catch(() => toast.error("Could not copy — select the link manually."));
                                }}
                                className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-4 text-xs font-semibold uppercase tracking-widest shrink-0"
                            >
                                <Copy size={12} /> Copy
                            </button>
                        </div>
                        {savedDraft.emailed && (
                            <div className="mt-3 inline-flex items-center gap-2 text-xs text-emerald-300" data-testid="draft-emailed-note">
                                <Mail size={12} /> We also emailed you the link.
                            </div>
                        )}
                        <button
                            type="button"
                            data-testid="draft-saved-close"
                            onClick={() => setSavedDraft(null)}
                            className="mt-8 text-xs font-mono-tech uppercase tracking-widest text-slate-400 hover:text-white transition-colors"
                        >
                            Keep editing
                        </button>
                    </div>
                )}

                {redirecting && (
                    <div
                        data-testid="checkout-redirect-overlay"
                        className="fixed inset-0 z-[60] bg-[#0F1E3B]/97 backdrop-blur-sm flex flex-col items-center justify-center text-center px-8"
                    >
                        <div className="w-12 h-12 border border-amber-400/50 flex items-center justify-center mb-5">
                            <Timer size={22} className="text-amber-300" />
                        </div>
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-amber-300 mb-3">
                            / Screens reserved
                        </div>
                        <div className="font-display font-black text-5xl text-white tracking-tight">
                            <HoldTicker iso={redirecting.expiresAt} testid="checkout-redirect-countdown" />
                        </div>
                        <p className="mt-4 text-sm text-slate-400 max-w-sm">
                            {redirecting.heldCount} screen{redirecting.heldCount === 1 ? " is" : "s are"} held exclusively for you while you pay.
                            If payment doesn&apos;t complete before the timer ends, the hold releases automatically.
                        </p>
                        <div className="mt-8 flex items-center gap-2 text-xs text-slate-500">
                            <Loader2 size={14} className="animate-spin" /> Taking you to secure Stripe checkout…
                        </div>
                        <a
                            href={redirecting.url}
                            data-testid="checkout-redirect-continue"
                            className="mt-4 inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-widest"
                        >
                            <Lock size={12} /> Continue to Stripe now
                        </a>
                    </div>
                )}

                <FormHeader onClose={handleClose} />

                {activeHold && !done && !redirecting && (
                    <div
                        data-testid="checkout-hold-banner"
                        className="mx-8 mt-4 flex items-center gap-3 border border-amber-400/40 bg-amber-400/10 px-4 py-3"
                    >
                        <Timer size={16} className="text-amber-300 shrink-0" />
                        <p className="text-xs text-amber-200 flex-1">
                            {activeHold.held_count} screen{activeHold.held_count === 1 ? "" : "s"} from your previous checkout {activeHold.held_count === 1 ? "is" : "are"} still reserved for{" "}
                            <HoldTicker
                                iso={activeHold.expires_at}
                                onExpire={() => { clearHoldTimer(); setActiveHold(null); }}
                                testid="checkout-hold-banner-countdown"
                                className="font-mono-tech text-amber-300 font-semibold"
                            />
                            . Complete payment before the hold ends.
                        </p>
                    </div>
                )}

                {!done && <ProgressBar step={step} totalSteps={totalSteps} progress={progress} />}

                <form onSubmit={(e) => e.preventDefault()} className="px-8 py-8 min-h-[460px] flex flex-col">
                    {done ? (
                        <SuccessState onClose={handleClose} />
                    ) : (
                        <>
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentStepId}
                                    initial={{ opacity: 0, x: 16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -16 }}
                                    transition={{ duration: 0.3 }}
                                    className="flex-1"
                                    data-testid={NOXN.formStep(step + 1)}
                                >
                                    {/* GOAL */}
                                    {currentStepId === "goal" && (
                                        <Step title="What's the campaign goal?" subtitle="Pick the one that fits best.">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {goals.map((g) => (
                                                    <OptionButton
                                                        key={g.value}
                                                        active={values.campaign_goal === g.value}
                                                        onClick={() => setValue("campaign_goal", g.value, { shouldValidate: true })}
                                                        testId={NOXN.formField(`goal-${g.value}`)}
                                                    >{g.label}</OptionButton>
                                                ))}
                                            </div>
                                            <FieldError msg={errors.campaign_goal?.message} />
                                            <AnimatePresence>
                                                {isOther && (
                                                    <motion.div
                                                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                                                        animate={{ opacity: 1, height: "auto", marginTop: 24 }}
                                                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                                                        transition={{ duration: 0.25 }}
                                                    >
                                                        <input
                                                            {...register("goal_other_text")}
                                                            type="text" autoFocus
                                                            placeholder="Tell us your campaign goal"
                                                            data-testid={NOXN.formField("goal_other_text")}
                                                            className="w-full bg-[#1B2E55] border border-white/10 text-white px-4 py-3 text-sm placeholder:text-slate-500 focus:border-[#FF5A00] transition-colors"
                                                        />
                                                        <FieldError msg={errors.goal_other_text?.message} />
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </Step>
                                    )}

                                    {/* PLACEMENT — cities, areas + screen types */}
                                    {currentStepId === "placement" && (
                                        <Step title="Where should your campaign run?" subtitle="Pick your cities and the screen types you're after.">
                                            <div className="space-y-8">
                                                <div>
                                                    <div className="flex items-baseline justify-between">
                                                        <Label>Cities</Label>
                                                        <span className="text-[10px] font-mono-tech uppercase tracking-[0.25em] text-slate-500">
                                                            {values.ad_space?.cities?.length || 0} selected
                                                        </span>
                                                    </div>
                                                    <div className="mt-2 flex flex-wrap gap-2" data-testid={NOXN.formField("ad_space_cities")}>
                                                        {AU_CITIES.map((c) => {
                                                            const selected = values.ad_space?.cities?.includes(c);
                                                            return (
                                                                <button
                                                                    type="button" key={c}
                                                                    onClick={() => toggleCity(c)}
                                                                    data-testid={NOXN.formField(`city-${c.toLowerCase()}`)}
                                                                    className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-mono-tech uppercase tracking-widest border transition-all ${
                                                                        selected
                                                                            ? "border-[#FF5A00] bg-[#FF5A00]/10 text-white"
                                                                            : "border-white/10 hover:border-white/30 text-slate-300 hover:text-white"
                                                                    }`}
                                                                >
                                                                    <span className={`w-3 h-3 border flex items-center justify-center ${selected ? "border-[#FF5A00] bg-[#FF5A00]" : "border-white/25"}`}>
                                                                        {selected && <Check size={10} className="text-white" strokeWidth={3.5} />}
                                                                    </span>
                                                                    {c}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                    <FieldError msg={errors.ad_space?.cities?.message} />
                                                </div>

                                                <div>
                                                    <Label>Preferred suburbs / areas (optional)</Label>
                                                    <input
                                                        type="text"
                                                        {...register("ad_space.preferred_suburbs")}
                                                        data-testid={NOXN.formField("ad_space_preferred_suburbs")}
                                                        placeholder="e.g. Fortitude Valley, South Yarra, Bondi Junction"
                                                        className="mt-2 w-full bg-[#1B2E55] border border-white/10 text-white px-4 py-3 text-sm placeholder:text-slate-500 focus:border-[#FF5A00] transition-colors"
                                                    />
                                                </div>

                                                <div>
                                                    <div className="flex items-baseline justify-between">
                                                        <Label>Screen types</Label>
                                                        <span className="text-[10px] font-mono-tech uppercase tracking-[0.25em] text-slate-500">
                                                            {values.ad_space?.screen_types?.length || 0} selected
                                                        </span>
                                                    </div>
                                                    <div className="mt-2 flex flex-wrap gap-2" data-testid={NOXN.formField("ad_space_screen_types")}>
                                                        {SCREEN_TYPES.map((s) => {
                                                            const selected = values.ad_space?.screen_types?.includes(s);
                                                            return (
                                                                <button
                                                                    type="button" key={s}
                                                                    data-testid={NOXN.formField(`screen-type-${s.replace(/\s+/g, "-").toLowerCase()}`)}
                                                                    onClick={() => toggleScreenType(s)}
                                                                    className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-mono-tech uppercase tracking-widest border transition-all ${
                                                                        selected
                                                                            ? "border-[#FF5A00] bg-[#FF5A00]/10 text-white"
                                                                            : "border-white/10 hover:border-white/30 text-slate-300 hover:text-white"
                                                                    }`}
                                                                >
                                                                    <span className={`w-3 h-3 border flex items-center justify-center ${selected ? "border-[#FF5A00] bg-[#FF5A00]" : "border-white/25"}`}>
                                                                        {selected && <Check size={10} className="text-white" strokeWidth={3.5} />}
                                                                    </span>
                                                                    {s}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                    <FieldError msg={errors.ad_space?.screen_types?.message} />
                                                </div>
                                            </div>
                                        </Step>
                                    )}

                                    {/* Launch date + budget */}
                                    {currentStepId === "date_budget" && (
                                        <Step
                                            title="Launch date & budget"
                                            subtitle="Set a media budget (AUD) and target go-live date. Campaigns go live at 00:00 in each screen's local time zone."
                                        >
                                            <div className="space-y-10">
                                                <LaunchDatePicker control={control} errors={errors} />
                                                <BudgetSlider control={control} values={values} errors={errors} />
                                            </div>
                                        </Step>
                                    )}

                                    {/* CONTACT */}
                                    {currentStepId === "contact" && (
                                        <Step title="Tell us about you" subtitle="We'll send a tailored plan within one business day.">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <FormInput label="Business name" register={register("business_name")} testId={NOXN.formField("business_name")} error={errors.business_name?.message} />
                                                <FormInput label="Your name" register={register("contact_name")} testId={NOXN.formField("contact_name")} error={errors.contact_name?.message} />
                                                <FormInput label="Email" type="email" register={register("email")} testId={NOXN.formField("email")} error={errors.email?.message} />
                                                <FormInput label="Phone (optional)" register={register("phone")} testId={NOXN.formField("phone")} />
                                            </div>
                                            <div className="mt-6">
                                                <Label>Anything else? (Optional)</Label>
                                                <textarea
                                                    {...register("notes")}
                                                    data-testid={NOXN.formField("notes")}
                                                    rows={5}
                                                    maxLength={MAX_NOTES}
                                                    placeholder="Creative references, deadlines, dream screens, anything we should know..."
                                                    className="mt-2 w-full bg-[#1B2E55] border border-white/10 text-white p-4 text-sm placeholder:text-slate-500 focus:border-[#FF5A00] transition-colors"
                                                />
                                                {MAX_NOTES - (values.notes?.length || 0) <= 100 && (
                                                    <div
                                                        data-testid="notes-remaining"
                                                        className={`mt-2 text-xs font-mono-tech uppercase tracking-widest ${
                                                            (values.notes?.length || 0) >= MAX_NOTES ? "text-[#FF5A00]" : "text-slate-400"
                                                        }`}
                                                    >
                                                        {MAX_NOTES - (values.notes?.length || 0)} characters remaining
                                                    </div>
                                                )}
                                                <FieldError msg={errors.notes?.message} />
                                            </div>
                                        </Step>
                                    )}

                                    {/* SUMMARY */}
                                    {currentStepId === "summary" && (
                                        <Step title="Review your request" subtitle="Everything look right? You can go back to edit any step.">
                                            <SummaryPanel
                                                values={values}
                                                breakdown={buildBreakdown()}
                                                selectedScreenIds={selectedScreenIds}
                                                onScreensChange={setSelectedScreenIds}
                                            />
                                        </Step>
                                    )}
                                </motion.div>
                            </AnimatePresence>

                            <FormNav
                                step={step}
                                totalSteps={totalSteps}
                                currentStepId={currentStepId}
                                onNext={next}
                                onPrev={prev}
                                onSubmit={handleSubmit(onSubmit)}
                                submitting={submitting}
                                onSaveDraft={saveDraft}
                                savingDraft={savingDraft}
                            />
                        </>
                    )}
                </form>
            </DialogContent>
        </Dialog>
    );
};

// ============================================================
// Sub-components
// ============================================================
const FormHeader = ({ onClose }) => (
    <div className="flex items-center justify-between px-8 pt-8 pb-6 border-b border-white/5">
        <div>
            <div className="flex items-center gap-2">
                <NoxnLogo variant="auto" className="h-4 w-auto" />
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#FF5A00]">/ Campaign Request</span>
            </div>
            <div className="mt-1 text-white font-display text-xl tracking-tight">
                Let&apos;s start prepping your campaign
            </div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white transition" data-testid="form-close-btn" aria-label="Close">
            <X size={20} />
        </button>
    </div>
);

const ProgressBar = ({ step, totalSteps, progress }) => (
    <div className="px-8 pt-6">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.3em] font-mono-tech text-slate-500 mb-3">
            <span>Step {step + 1} of {totalSteps}</span>
            <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-px bg-white/10 relative overflow-hidden">
            <div className="h-full bg-[#FF5A00] transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
    </div>
);

const FormNav = ({ step, totalSteps, currentStepId, onNext, onPrev, onSubmit, submitting, onSaveDraft, savingDraft }) => (
    <div className="pt-8 mt-auto border-t border-white/5 flex items-center justify-between gap-3">
        <button
            type="button" onClick={onPrev} disabled={step === 0}
            data-testid={NOXN.formPrevBtn}
            className="inline-flex items-center gap-2 text-sm font-mono-tech uppercase tracking-widest text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
            <ArrowLeft size={16} /> Back
        </button>

        {step >= 1 && (
            <button
                type="button" onClick={onSaveDraft} disabled={savingDraft}
                data-testid="wizard-save-draft-btn"
                className="inline-flex items-center gap-2 text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 hover:text-[#FF5A00] disabled:opacity-40 transition-colors"
            >
                {savingDraft ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Save &amp; finish later
            </button>
        )}

        {currentStepId !== "summary" ? (
            <button
                type="button" onClick={onNext}
                data-testid={NOXN.formNextBtn}
                className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-6 py-3 text-sm font-semibold uppercase tracking-widest transition-colors"
            >
                Continue <ArrowRight size={16} />
            </button>
        ) : (
            <button
                type="button" onClick={onSubmit} disabled={submitting}
                data-testid={NOXN.formSubmitBtn}
                className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-60 text-white px-6 py-3 text-sm font-semibold uppercase tracking-widest transition-colors"
            >
                {submitting ? (<><Loader2 size={16} className="animate-spin" /> Sending</>) : (<>Submit campaign request <Check size={16} /></>)}
            </button>
        )}
    </div>
);

// ---- Launch date + Budget ----
const LaunchDatePicker = ({ control, errors }) => {
    const [open, setOpen] = useState(false);
    return (
        <div>
            <Label>Launch date</Label>
            <Controller
                control={control}
                name="launch_date"
                render={({ field }) => (
                    <div className="mt-2">
                        <button
                            type="button"
                            onClick={() => setOpen((v) => !v)}
                            data-testid={NOXN.formField("launch_date")}
                            aria-expanded={open}
                            className="w-full flex items-center justify-between bg-[#1B2E55] border border-white/10 hover:border-white/30 focus:border-[#FF5A00] text-white text-sm px-4 py-3 transition-colors"
                        >
                            <span className={field.value ? "text-white" : "text-slate-500"}>
                                {field.value ? format(field.value, "PPP") : "Pick a launch date"}
                            </span>
                            <CalendarIcon size={16} className="text-[#FF5A00]" />
                        </button>
                        <AnimatePresence initial={false}>
                            {open && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                                    className="overflow-hidden"
                                >
                                    <div className="mt-2 bg-[#1B2E55] border border-white/10 text-white flex items-center justify-center py-2">
                                        <Calendar
                                            mode="single"
                                            selected={field.value}
                                            onSelect={(d) => {
                                                field.onChange(d);
                                                if (d) setOpen(false);
                                            }}
                                            disabled={(d) => d <= startOfToday()}
                                            initialFocus
                                        />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )}
            />
            <FieldError msg={errors.launch_date?.message} />
        </div>
    );
};

const BudgetSlider = ({ control, values, errors }) => (
    <div>
        <div className="flex items-baseline justify-between">
            <Label>Media budget</Label>
            <div data-testid="budget-value" className="font-display font-black tracking-tighter text-white text-2xl">
                {formatAud(values.budget_amount_aud || 250)}{" "}
                <span className="text-xs text-slate-500 font-mono-tech uppercase tracking-widest align-middle">AUD</span>
            </div>
        </div>
        <div className="mt-4">
            <Controller
                control={control}
                name="budget_amount_aud"
                render={({ field }) => (
                    <Slider
                        data-testid={NOXN.formField("budget_slider")}
                        min={250} max={20000} step={50}
                        value={[field.value ?? 250]}
                        onValueChange={(v) => field.onChange(v[0])}
                        className="w-full"
                    />
                )}
            />
            <div className="mt-2 flex justify-between text-[10px] font-mono-tech uppercase tracking-[0.25em] text-slate-500">
                <span>$250 AUD</span><span>$20,000 AUD</span>
            </div>
        </div>
        <FieldError msg={errors.budget_amount_aud?.message} />
    </div>
);

// ---- Summary ----
const SummaryPanel = ({ values, breakdown, selectedScreenIds, onScreensChange }) => {
    const goalLabel = goals.find((g) => g.value === values.campaign_goal)?.label || values.campaign_goal;
    const launchIso = values.launch_date instanceof Date
        ? format(values.launch_date, "yyyy-MM-dd")
        : values.launch_date;

    return (
        <div className="space-y-6" data-testid="request-summary">
            {/* Screen picker (Phase 3) */}
            {launchIso && (
                <ScreensPicker
                    cities={values.ad_space?.cities || []}
                    launchDate={launchIso}
                    selectedIds={selectedScreenIds}
                    onChange={onScreensChange}
                />
            )}

            {/* Campaign details */}
            <div className="space-y-3 text-sm">
                <SummaryRow label="Goal">
                    {values.campaign_goal === "other" ? values.goal_other_text : goalLabel}
                </SummaryRow>
                <SummaryRow label="Cities">
                    {(values.ad_space?.cities || []).join(", ") || "—"}
                </SummaryRow>
                {values.ad_space?.preferred_suburbs && (
                    <SummaryRow label="Preferred areas">{values.ad_space.preferred_suburbs}</SummaryRow>
                )}
                <SummaryRow label="Screen types">
                    {(values.ad_space?.screen_types || []).join(", ") || "—"}
                </SummaryRow>
                <SummaryRow label="Launch date">
                    {values.launch_date ? format(values.launch_date, "PPP") : "—"}
                </SummaryRow>
                <SummaryRow label="Business">
                    {values.business_name} · {values.contact_name} · {values.email}
                    {values.phone && ` · ${values.phone}`}
                </SummaryRow>
                {values.notes && <SummaryRow label="Notes">{values.notes}</SummaryRow>}
            </div>

            {/* Pricing breakdown */}
            {breakdown && breakdown.subtotal >= 100 && (
                <div
                    data-testid="pricing-breakdown"
                    className="border border-[#FF5A00]/30 bg-[#1B2E55]/70 p-5"
                >
                    <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-[#FF5A00] mb-3">
                        / Payment summary
                    </div>
                    <ul className="space-y-2 text-sm">
                        {breakdown.items.map((li) => (
                            <li key={li.label} className="flex items-baseline justify-between gap-4">
                                <span className="text-slate-300">{li.label}</span>
                                <span className="font-mono-tech text-white">
                                    ${(li.amount_cents / 100).toFixed(2)}
                                </span>
                            </li>
                        ))}
                        <li className="flex items-baseline justify-between gap-4 pt-2 border-t border-white/10">
                            <span className="text-slate-400">Subtotal</span>
                            <span className="font-mono-tech text-white">
                                ${(breakdown.subtotal / 100).toFixed(2)}
                            </span>
                        </li>
                        <li className="flex items-baseline justify-between gap-4">
                            <span className="text-slate-400">GST (10%)</span>
                            <span className="font-mono-tech text-white">
                                ${(breakdown.gst / 100).toFixed(2)}
                            </span>
                        </li>
                        {breakdown.discount > 0 && (
                            <li className="flex items-baseline justify-between gap-4" data-testid="pricing-referral-discount">
                                <span className="text-emerald-300">Referral discount</span>
                                <span className="font-mono-tech text-emerald-300">
                                    −${(breakdown.discount / 100).toFixed(2)}
                                </span>
                            </li>
                        )}
                        {breakdown.credit > 0 && (
                            <li className="flex items-baseline justify-between gap-4" data-testid="pricing-account-credit">
                                <span className="text-emerald-300">Account credit</span>
                                <span className="font-mono-tech text-emerald-300">
                                    −${(breakdown.credit / 100).toFixed(2)}
                                </span>
                            </li>
                        )}
                        <li
                            data-testid="pricing-total"
                            className="flex items-baseline justify-between gap-4 pt-3 border-t border-[#FF5A00]/40"
                        >
                            <span className="font-display font-bold text-white uppercase tracking-widest text-xs">
                                Total payable · AUD
                            </span>
                            <span className="font-display font-black tracking-tighter text-2xl text-white">
                                ${(breakdown.total / 100).toFixed(2)}
                            </span>
                        </li>
                    </ul>
                    <div className="mt-4 text-[10px] font-mono-tech uppercase tracking-[0.25em] text-slate-500 leading-relaxed">
                        / Secure payment via Stripe · Cards, Link and PayPal (where enabled)
                    </div>
                </div>
            )}
        </div>
    );
};

const SummaryRow = ({ label, children }) => (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 py-3 border-b border-white/5">
        <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500">{label}</div>
        <div className="sm:col-span-3 text-slate-200 break-words">{children}</div>
    </div>
);

// ---- Phase 3: Screens picker ----
const ScreenLightbox = ({ open, onClose, images, initialIndex = 0, name }) => {
    const [idx, setIdx] = useState(initialIndex);
    useEffect(() => { if (open) setIdx(initialIndex); }, [open, initialIndex]);
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "ArrowRight") setIdx((i) => (i + 1) % images.length);
            if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + images.length) % images.length);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, images.length, onClose]);
    if (!open || !images.length) return null;
    const src = images[idx];
    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-6 bg-black/90 backdrop-blur-sm"
            onClick={onClose} data-testid="screen-lightbox">
            <button type="button" onClick={onClose} aria-label="Close"
                className="absolute top-6 right-6 text-slate-400 hover:text-white p-2"
                data-testid="screen-lightbox-close">
                <X size={20} />
            </button>
            <div className="relative max-w-4xl max-h-[85vh] w-full" onClick={(e) => e.stopPropagation()}>
                <img src={src} alt={name} className="w-full h-full object-contain max-h-[80vh]" />
                {images.length > 1 && (
                    <>
                        <button type="button" data-testid="screen-lightbox-prev"
                            onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-[#FF5A00] text-white p-3 transition-colors">
                            <ArrowLeft size={16} />
                        </button>
                        <button type="button" data-testid="screen-lightbox-next"
                            onClick={() => setIdx((i) => (i + 1) % images.length)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-[#FF5A00] text-white p-3 transition-colors">
                            <ArrowRight size={16} />
                        </button>
                    </>
                )}
                <div className="mt-4 flex items-center justify-between text-[11px] font-mono-tech uppercase tracking-widest text-slate-400">
                    <span>{name}</span>
                    <span>{idx + 1} / {images.length}</span>
                </div>
            </div>
        </div>
    );
};

const ScreensPicker = ({ cities, launchDate, selectedIds, onChange }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lightbox, setLightbox] = useState(null); // {images, name}

    // Compute the 7-day window for display
    const rangeLabel = useMemo(() => {
        if (!launchDate) return "";
        const s = new Date(launchDate + "T00:00:00");
        const e = new Date(s); e.setDate(e.getDate() + 6);
        const fmt = (d) => d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
        return `${fmt(s)} → ${fmt(e)}`;
    }, [launchDate]);

    useEffect(() => {
        if (!launchDate) return;
        let cancelled = false;
        (async () => {
            setLoading(true); setError(null);
            try {
                const start = launchDate;
                const s = new Date(launchDate + "T00:00:00");
                s.setDate(s.getDate() + 6);
                const end = `${s.getFullYear()}-${String(s.getMonth()+1).padStart(2,"0")}-${String(s.getDate()).padStart(2,"0")}`;
                const params = new URLSearchParams({ start, end, category: "cat2", limit: "12" });
                if (cities?.length) params.set("cities", cities.join(","));
                const { data } = await axios.get(`${API}/screens/public/recommend?${params.toString()}`);
                if (cancelled) return;
                setRows(data || []);
                // Pre-select all available screens the first time we load, but
                // don't clobber a user's explicit selection.
                if (!selectedIds || selectedIds.length === 0) {
                    onChange((data || []).filter((r) => r.available).map((r) => r.id));
                }
            } catch (e) {
                if (!cancelled) setError(e?.response?.data?.detail || "Could not load screens.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [launchDate, (cities || []).join("|")]);

    const toggle = (id) => {
        const next = selectedIds.includes(id)
            ? selectedIds.filter((x) => x !== id)
            : [...selectedIds, id];
        onChange(next);
    };

    if (!launchDate) return null;

    return (
        <div className="border border-[#FF5A00]/30 bg-[#1B2E55]/70 p-5" data-testid="screens-picker">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-[#FF5A00]">
                        / Screens to reserve
                    </div>
                    <div className="mt-1 text-xs text-slate-400 font-mono-tech">
                        7-day window · {rangeLabel}
                    </div>
                </div>
                <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500">
                    {selectedIds.length}/{rows.length} selected
                </div>
            </div>
            {loading ? (
                <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="screens-picker-loading">
                    <Loader2 size={14} className="animate-spin" /> Checking availability…
                </div>
            ) : error ? (
                <div className="text-xs text-[#FF5A00]" data-testid="screens-picker-error">{error}</div>
            ) : rows.length === 0 ? (
                <div className="text-xs text-slate-400" data-testid="screens-picker-empty">
                    No screens match your cities yet. Our team will curate a set for you after payment.
                </div>
            ) : (
                <ul className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                    {rows.map((r) => {
                        const checked = selectedIds.includes(r.id);
                        const images = (r.photo_urls && r.photo_urls.length ? r.photo_urls : (r.photo_url ? [r.photo_url] : []));
                        return (
                            <li key={r.id}>
                                <div
                                    data-testid={`screens-picker-item-${r.id}`}
                                    className={`flex items-center gap-3 border px-3 py-2.5 transition-colors ${
                                        checked ? "border-[#FF5A00] bg-[#FF5A00]/10" : "border-white/10 hover:border-white/30"
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggle(r.id)}
                                        data-testid={`screens-picker-check-${r.id}`}
                                        className="accent-[#FF5A00] cursor-pointer shrink-0"
                                    />
                                    {images.length ? (
                                        <button type="button"
                                            onClick={() => setLightbox({ images, name: r.name })}
                                            data-testid={`screens-picker-thumb-${r.id}`}
                                            aria-label={`View photos of ${r.name}`}
                                            className="relative w-14 h-14 shrink-0 border border-white/15 overflow-hidden hover:border-[#FF5A00] transition-colors group">
                                            <img src={images[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                            {images.length > 1 && (
                                                <span className="absolute bottom-0.5 right-0.5 bg-black/70 text-white text-[9px] font-mono-tech px-1 py-0.5">
                                                    +{images.length - 1}
                                                </span>
                                            )}
                                        </button>
                                    ) : (
                                        <div className="w-14 h-14 border border-white/10 bg-[#0F1E3B] shrink-0" />
                                    )}
                                    <label className="flex-1 min-w-0 cursor-pointer" onClick={() => toggle(r.id)}>
                                        <div className="text-sm text-white truncate">{r.name}</div>
                                        <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 truncate">
                                            {r.city} · {r.location}
                                        </div>
                                    </label>
                                    <div className="text-right shrink-0">
                                        <div className="text-xs font-mono-tech text-white">
                                            ${(r.daily_rate_cents / 100).toLocaleString("en-AU")}/day
                                        </div>
                                        <div className={`text-[10px] font-mono-tech uppercase tracking-widest mt-0.5 ${
                                            r.available ? "text-emerald-400" : "text-[#FF5A00]"
                                        }`}>
                                            {r.available ? "Available" : "Waitlist"}
                                        </div>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
            <p className="mt-3 text-[10px] text-slate-500 leading-relaxed">
                Available screens are placed on a 20-minute hold when you continue to payment. Waitlisted screens
                are flagged for a NOXN director to swap on your behalf.
            </p>
            <ScreenLightbox
                open={!!lightbox}
                onClose={() => setLightbox(null)}
                images={lightbox?.images || []}
                name={lightbox?.name || ""}
            />
        </div>
    );
};

// ---- Shared primitives ----
const Step = ({ title, subtitle, children }) => (
    <div>
        <h3 className="font-display font-black tracking-tighter text-white text-2xl sm:text-3xl">{title}</h3>
        {subtitle && <p className="mt-2 text-sm text-slate-400">{subtitle}</p>}
        <div className="mt-8">{children}</div>
    </div>
);

const OptionButton = ({ active, onClick, children, testId }) => (
    <button
        type="button" onClick={onClick} data-testid={testId}
        className={`text-left border transition-all px-5 py-4 text-sm ${active ? "border-[#FF5A00] bg-[#FF5A00]/10 text-white" : "border-white/10 hover:border-white/30 text-slate-300 hover:text-white"}`}
    >{children}</button>
);

const Label = ({ children }) => (
    <div className="text-[10px] uppercase tracking-[0.3em] font-mono-tech text-slate-500">{children}</div>
);

const FormInput = ({ label, type = "text", register, error, testId }) => (
    <div>
        <Label>{label}</Label>
        <input
            type={type} {...register} data-testid={testId}
            className="mt-2 w-full bg-[#1B2E55] border border-white/10 text-white px-4 py-3 text-sm placeholder:text-slate-500 focus:border-[#FF5A00] transition-colors"
        />
        <FieldError msg={error} />
    </div>
);

const FieldError = ({ msg }) => (msg ? <div className="mt-2 text-xs text-[#FF5A00]">{msg}</div> : null);

const SuccessState = ({ onClose }) => (
    <div data-testid={NOXN.formSuccess} className="flex-1 flex flex-col items-center justify-center text-center py-10">
        <div className="w-16 h-16 border border-[#FF5A00] flex items-center justify-center mb-8 border-glow-orange">
            <Check className="text-[#FF5A00]" size={28} strokeWidth={2.5} />
        </div>
        <h3 className="font-display font-black tracking-tighter text-white text-3xl">We&apos;ve got it.</h3>
        <p className="mt-4 text-slate-400 max-w-md text-sm">
            A NOXN campaign director will reach out within one business day with a tailored plan
            and screen recommendations. Keep an eye on your inbox.
        </p>

        <div
            data-testid="success-disclaimer"
            className="mt-8 max-w-md text-left border-l-2 border-[#FF5A00]/60 bg-[#1B2E55]/40 px-4 py-3"
        >
            <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-[#FF5A00] mb-1">
                / Disclaimer
            </div>
            <p className="text-xs text-slate-300 leading-relaxed">
                The selected campaign dates and screen locations are subject to final confirmation.
                Availability depends on creative production timeframes, screen approval, and screen
                availability at the time of scheduling.
            </p>
        </div>

        <button
            type="button" onClick={onClose}
            className="mt-10 inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white px-6 py-3 text-sm font-semibold uppercase tracking-widest transition-colors"
        >Close</button>
    </div>
);
